<template>
  <div
    class="cycles-empty-state gap-3"
    :class="`cycles-empty-state--${variant}`"
    :data-testid="rootTestId"
  >
    <div class="cycles-empty-state__icon-wrap" aria-hidden="true">
      <div class="cycles-empty-state__icon-circle">
        <img
          :src="iconSrc"
          alt=""
          class="cycles-empty-state__icon"
          data-testid="cycles-page__empty-icon"
        />
      </div>
    </div>

    <h3 class="cycles-empty-state__title" data-testid="cycles-page__empty-title">
      {{ title }}
    </h3>

    <p class="cycles-empty-state__description" data-testid="cycles-page__empty-description">
      {{ description }}
    </p>

    <v-btn
      v-if="showAction"
      color="primary"
      class="cycles-empty-state__action text-none white--text"
      :disabled="actionDisabled"
      :data-testid="actionTestId"
      @click="$emit('action')"
    >
      <v-icon v-if="variant === 'no-cycles'" left small>mdi-plus</v-icon>
      {{ actionLabel }}
    </v-btn>
  </div>
</template>

<script>
import './CyclesEmptyState.scss';

const ICON_NO_CYCLES = '/img/icons/fbc-icons/NoCycleAdd.svg';
const ICON_NO_RESULTS = '/img/icons/fbc-icons/NoCycle.svg';

export default {
  name: 'CyclesEmptyState',

  props: {
    /**
     * 'no-cycles' — zero cycles in the system
     * 'period-filter' — period filter active, no matches
     * 'filters' — other filters active, no matches
     */
    variant: {
      type: String,
      default: 'no-cycles',
      validator: (v) => ['no-cycles', 'period-filter', 'filters'].includes(v),
    },
    /** e.g. "March 2025" for period-filter copy */
    periodLabel: {
      type: String,
      default: '',
    },
    canAdd: {
      type: Boolean,
      default: false,
    },
    actionDisabled: {
      type: Boolean,
      default: false,
    },
    hideAction: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    rootTestId() {
      if (this.variant === 'no-cycles') {
        return 'bookclosure-project-list__empty-state';
      }
      if (this.variant === 'period-filter') {
        return 'cycles-page__empty-period-filter';
      }
      return 'cycles-page__empty-filters';
    },

    iconSrc() {
      return this.variant === 'no-cycles' ? ICON_NO_CYCLES : ICON_NO_RESULTS;
    },

    title() {
      if (this.variant === 'no-cycles') return 'No Close Cycles Yet';
      if (this.variant === 'period-filter') return 'No cycles found for this period';
      return 'No cycles found';
    },

    description() {
      if (this.variant === 'no-cycles') {
        return 'Create a new cycle to define your reporting period, assign tasks, and monitor your team\'s progress.';
      }
      if (this.variant === 'period-filter') {
        const period = this.periodLabel || 'the selected period';
        return `There are no close cycles scheduled for ${period}. Try selecting a different period or clearing your filters to see all cycles.`;
      }
      return 'No close cycles match your current filters. Try adjusting search, status, location, or users — or clear filters to see all cycles.';
    },

    showAction() {
      if (this.hideAction) return false;
      if (this.variant === 'no-cycles') return this.canAdd;
      return true;
    },

    actionLabel() {
      if (this.variant === 'no-cycles') return 'Add Cycle';
      if (this.variant === 'period-filter') return 'Clear Period Filter';
      return 'Clear Filters';
    },

    actionTestId() {
      if (this.variant === 'no-cycles') {
        return 'bookclosure-project-list__empty-add-cycle-btn';
      }
      if (this.variant === 'period-filter') {
        return 'cycles-page__empty-clear-period-btn';
      }
      return 'cycles-page__empty-clear-filters-btn';
    },
  },
};
</script>
